import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { CaretDown } from "@phosphor-icons/react";
import { Icon } from "./icon";

const labelVariants = cva(
  "ui-absolute ui-left-2.5 ui-text-gray-medium ui-transition-all ui-duration-200 ui-pointer-events-none",
  {
    variants: {
      state: {
        default: "ui-text-sm ui-top-[50%] ui-translate-y-[-50%]",
        shrunk: "ui-top-0 ui-translate-y-0 ui-text-text-xxs-medium",
      },
    },
    defaultVariants: {
      state: "default",
    },
  }
);

export interface BaseSelectFieldProps
  extends Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    "label" | "variant" | "dimension" | "placeholder"
  > {
  label?: string;
  variant?: "standard";
  error?: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string } | Record<string, any>>;
  optionKey?: string;
  optionValue?: string;
}

const BaseSelectField = React.forwardRef<
  HTMLSelectElement,
  BaseSelectFieldProps
>(
  (
    {
      className,
      label,
      value,
      defaultValue,
      error,
      options = [],
      optionKey = "value",
      optionValue = "label",
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState(
      value ?? defaultValue ?? ""
    );
    const hasValue = Boolean(internalValue) || Boolean(value);
    const shouldShrinkLabel = focused || hasValue;

    // Handle controlled and uncontrolled selects
    const selectValue = value !== undefined ? value : internalValue;

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (value === undefined) {
        setInternalValue(e.target.value);
      }
      props.onChange?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
      setFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
      setFocused(false);
      props.onBlur?.(e);
    };

    const selectId = props.id || `select-${React.useId()}`;
    const labelId = label ? `label-${selectId}` : undefined;
    const hasLabel = Boolean(label);

    return (
      <div className={cn("ui-relative ui-w-full", className)}>
        <div className="ui-relative">
          <select
            {...props}
            id={selectId}
            ref={ref}
            value={selectValue}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            aria-labelledby={labelId}
            className={cn(
              "ui-h-[47px] ui-w-full !ui-rounded-none ui-border-0 ui-border-b-1 ui-border-black-10 ui-bg-transparent ui-pr-6 ui-pt-2 ui-pb-1 ui-text-text-sm-regular ui-leading-normal ui-transition-all ui-duration-200 hover:ui-border-black-20 focus:ui-border-primary-dark focus:ui-border-b-1 focus:ui-outline-none focus:ui-shadow-[0_2px_0px_0px_color-mix(in_srgb,var(--flo-primary-dark-color)_20%,transparent)] ui-appearance-none",
              props.disabled &&
                "ui-border-gray-light ui-opacity-50 ui-cursor-not-allowed",
              error &&
                "ui-border-red-dark focus:ui-border-red-dark focus:ui-shadow-[0_2px_0px_0px_color-mix(in_srgb,var(--flo-red-dark-color)_20%,transparent)]",
              hasLabel && !shouldShrinkLabel && "ui-pl-2.5",
              hasLabel && shouldShrinkLabel && "ui-pl-2.5",
              !hasLabel && "ui-pl-2.5"
            )}
          >
            {props.placeholder && (
              <option value="" disabled>
                {props.placeholder}
              </option>
            )}
            {options.map((option, index) => {
              if (
                option &&
                typeof option === "object" &&
                "value" in option &&
                "label" in option
              ) {
                return (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                );
              }
              const optionVal =
                option && typeof option === "object"
                  ? (option as Record<string, any>)[optionKey]
                  : String(option);
              const optionLabel =
                option && typeof option === "object"
                  ? (option as Record<string, any>)[optionValue]
                  : String(option);
              return (
                <option key={optionVal || index} value={optionVal}>
                  {optionLabel}
                </option>
              );
            })}
          </select>
          <Icon
            IconComponent={CaretDown}
            size="xxs"
            className="ui-pointer-events-none ui-absolute ui-right-2.5 ui-top-1/2 ui--translate-y-1/2 ui-text-zinc-400"
          />
        </div>
        {label && (
          <label
            htmlFor={selectId}
            id={labelId}
            className={cn(
              labelVariants({
                state: shouldShrinkLabel ? "shrunk" : "default",
              }),
              focused && shouldShrinkLabel && "ui-text-primary-dark",
              props.disabled && "ui-opacity-50",
              error && "ui-text-red-dark"
            )}
          >
            {label}
            {props.required && " *"}
          </label>
        )}
      </div>
    );
  }
);

BaseSelectField.displayName = "BaseSelectField";

export { BaseSelectField };
